import { Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterOutlet, RouterLink, RouterLinkActive, Router } from '@angular/router';
import { AuthService } from '../../../core/services/auth.service';
import { ToastService } from '../../../core/services/toast.service';
import { ToastComponent } from '../toast/toast.component';
import { PolicyConsentModalComponent, SlaPolicyConsent } from '../policy-consent/policy-consent-modal.component';

@Component({
  selector: 'app-layout',
  standalone: true,
  imports: [CommonModule, RouterOutlet, RouterLink, RouterLinkActive, ToastComponent, PolicyConsentModalComponent],
  template: `
    <div class="h-screen flex flex-col overflow-hidden bg-slate-50 text-slate-800 font-sans relative">
      <app-toast></app-toast>

      <!-- MANDATORY FIRST-LOGIN SLA MODAL (BLOCKING & MODAL OVERLAY) -->
      @if (authService.hasInstitution() && !authService.isSlaAccepted()) {
        <div class="fixed inset-0 z-50 overflow-y-auto bg-slate-950/80 backdrop-blur-md flex items-center justify-center p-4 sm:p-6 animate-in fade-in duration-300">
          <div class="relative w-full max-w-2xl bg-white rounded-3xl shadow-2xl border border-slate-200 p-6 sm:p-8 animate-in zoom-in-95 duration-200">
            <app-policy-consent-modal
              [institutionName]="authService.institutionName()"
              [isReadOnly]="false"
              (accepted)="onAcceptMandatorySla($event)">
            </app-policy-consent-modal>
          </div>
        </div>
      }

      <!-- READ-ONLY SLA REVIEW MODAL (ACCESSIBLE ANYTIME FOR REVIEW) -->
      @if (showReviewModal()) {
        <div class="fixed inset-0 z-50 overflow-y-auto bg-slate-950/60 backdrop-blur-sm flex items-center justify-center p-4 sm:p-6 animate-in fade-in duration-200">
          <div class="relative w-full max-w-2xl bg-white rounded-3xl shadow-2xl border border-slate-200 p-6 sm:p-8 animate-in zoom-in-95 duration-200">
            <app-policy-consent-modal
              title="Institution Service Level Agreement (SLA) & Policy"
              [institutionName]="authService.institutionName()"
              [isReadOnly]="true"
              [agreedAt]="authService.slaAcceptedAt()"
              [agreedBy]="authService.slaAcceptedBy() || authService.userName()"
              (closed)="closeSlaReview()">
            </app-policy-consent-modal>
          </div>
        </div>
      }

      <!-- Top Header Navigation Bar (Fixed Height & Position) -->
      <header class="h-16 shrink-0 z-40 bg-white/95 backdrop-blur-md border-b border-slate-200 px-4 lg:px-8 py-3 flex items-center justify-between shadow-sm">
        <!-- Brand & Logo -->
        <div class="flex items-center gap-4">
          <button (click)="toggleMobileSidebar()" class="lg:hidden p-2 text-slate-600 hover:text-slate-900 rounded-lg bg-slate-100">
            <svg class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          
          <div class="flex items-center gap-3">
            <div class="w-10 h-10 rounded-xl bg-gradient-to-tr from-emerald-700 via-emerald-600 to-teal-500 flex items-center justify-center text-white font-black text-xl shadow-md shadow-emerald-600/20 ring-1 ring-white">
              ZX
            </div>
            <div>
              <div class="flex items-center gap-2">
                <span class="font-extrabold text-lg tracking-tight bg-gradient-to-r from-slate-900 via-emerald-900 to-emerald-700 bg-clip-text text-transparent">
                  ZAN-X CHANGE
                </span>
                <span class="text-[10px] font-bold tracking-widest uppercase px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">
                  e-Gov Portal
                </span>
              </div>
              <p class="text-xs text-slate-500 font-medium">Consent & Data Sharing Management</p>
            </div>
          </div>
        </div>

        <!-- Right Action Items: User Avatar & Sign Out -->
        <div class="flex items-center gap-3 lg:gap-5">
          <!-- SLA Active Reminder Pill (Read-Only Review Trigger) -->
          @if (authService.hasInstitution() && authService.isSlaAccepted()) {
            <button
              type="button"
              (click)="openSlaReview()"
              title="Click to review agreed Master SLA & Policies"
              class="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-xl bg-emerald-50 hover:bg-emerald-100/80 border border-emerald-200 text-emerald-800 text-xs font-bold transition shadow-xs group"
            >
              <span class="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
              <span class="text-[11px] font-semibold group-hover:text-emerald-900">SLA Verified (99.9%)</span>
              <svg class="w-3.5 h-3.5 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            </button>
          }

          <!-- User Profile & Rank Badge -->
          <a
            routerLink="/profile"
            title="Manage Profile & Security"
            class="flex items-center gap-3 pl-3 group cursor-pointer transition hover:opacity-95"
          >
            <div class="w-9 h-9 rounded-full bg-gradient-to-br from-emerald-100 to-teal-100 border border-emerald-300 flex items-center justify-center text-sm font-bold text-emerald-800 shadow-sm group-hover:ring-2 group-hover:ring-emerald-500/40 transition-all">
              {{ getUserInitials() }}
            </div>
            <div class="hidden sm:block text-left">
              <div class="text-xs font-bold text-slate-900 group-hover:text-emerald-700 transition-colors">{{ authService.userName() }}</div>
              <div class="text-[10px] text-emerald-700 font-bold flex items-center gap-1">
                <span class="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                {{ authService.primaryRole() }}
              </div>
            </div>
          </a>

          <!-- Logout Button -->
          <button
            (click)="logout()"
            title="Sign Out"
            class="p-2 rounded-lg text-slate-500 hover:text-rose-600 hover:bg-rose-50 transition-colors"
          >
            <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013 3h4a3 3 0 013 3v1" />
            </svg>
          </button>
        </div>
      </header>

      <!-- Main Shell Container (LOCKED/INACTIVE IF SLA IS PENDING) -->
      <div
        class="flex-1 flex min-h-0 overflow-hidden relative transition-all duration-300"
        [ngClass]="{
          'pointer-events-none select-none opacity-25 filter blur-[2px]': authService.hasInstitution() && !authService.isSlaAccepted()
        }"
      >
        <!-- Mobile Sidebar Backdrop Overlay -->
        @if (mobileSidebarOpen()) {
          <div
            class="fixed inset-0 top-16 bg-slate-950/40 backdrop-blur-xs z-20 lg:hidden transition-opacity"
            (click)="closeMobileSidebar()"
          ></div>
        }

        <!-- Sidebar Navigation (Stationary/Fixed, Does Not Move On Scroll) -->
        <aside
          class="w-64 h-full bg-white border-r border-slate-200 flex flex-col justify-between transition-all duration-300 z-30 shrink-0 shadow-sm"
          [ngClass]="{ 'hidden lg:flex': !mobileSidebarOpen(), 'flex fixed inset-y-0 left-0 top-16 z-30 bg-white shadow-2xl': mobileSidebarOpen() }"
        >
          <div class="p-4 space-y-6 overflow-y-auto flex-1">
            <!-- Active Institution Badge Card & SLA Status Reminder -->
            <div class="p-3.5 rounded-xl bg-gradient-to-b from-emerald-50/90 to-teal-50/50 border border-emerald-200/80 shadow-sm space-y-2.5">
              <div>
                <div class="text-[10px] font-bold text-emerald-700 uppercase tracking-widest">Active Institution</div>
                <div class="text-xs font-bold text-slate-900 mt-0.5 line-clamp-2">
                  {{ authService.institutionName() }}
                </div>
              </div>

              <!-- SLA Compliance Reminder Widget -->
              @if (authService.hasInstitution()) {
                @if (authService.isSlaAccepted()) {
                  <div class="pt-2 border-t border-emerald-200/60 flex items-center justify-between">
                    <span class="inline-flex items-center gap-1.5 text-[11px] font-bold text-emerald-800">
                      <span class="w-1.5 h-1.5 rounded-full bg-emerald-600"></span>
                      SLA Accepted
                    </span>
                    <button
                      type="button"
                      (click)="openSlaReview()"
                      class="text-[10px] font-bold text-emerald-700 hover:text-emerald-950 underline underline-offset-2 transition"
                    >
                      Review Terms
                    </button>
                  </div>
                } @else {
                  <div class="pt-2 border-t border-amber-200 flex items-center gap-1 text-[11px] font-bold text-amber-800">
                    <svg class="w-3.5 h-3.5 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <span>SLA Pending Action</span>
                  </div>
                }
              }
            </div>

            <!-- Navigation Links Section -->
            <nav class="space-y-1" (click)="closeMobileSidebar()">
              <div class="text-[10px] font-bold text-slate-400 uppercase tracking-wider px-3 mb-2">Main Navigation</div>

              <a
                routerLink="/dashboard"
                routerLinkActive="bg-emerald-50 text-emerald-800 border-emerald-400/60 font-bold shadow-sm"
                class="flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-xs text-slate-600 hover:text-emerald-700 hover:bg-emerald-50/60 transition-all border border-transparent nav-link-glow"
              >
                <svg class="w-4 h-4 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                </svg>
                Dashboard
              </a>

              <!-- Engine & Config Side Menu with Submenus (Positioned directly after Dashboard) -->
              <div>
                <button
                  type="button"
                  (click)="toggleEngineConfig($event)"
                  class="w-full flex items-center justify-between px-3.5 py-2.5 rounded-xl text-xs font-semibold transition-all border border-transparent cursor-pointer group"
                  [ngClass]="isEngineConfigActive()
                    ? 'bg-emerald-50/80 text-emerald-900 border-emerald-300/80 font-bold shadow-2xs'
                    : 'text-slate-600 hover:text-emerald-700 hover:bg-emerald-50/60'"
                >
                  <div class="flex items-center gap-3">
                    <svg class="w-4 h-4 text-emerald-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <span>Engine & Config</span>
                  </div>
                  <svg
                    class="w-3.5 h-3.5 text-slate-400 group-hover:text-slate-600 transition-transform duration-200"
                    [class.rotate-180]="engineConfigExpanded()"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                <!-- Submenu Items: 1. Prerequisites & Installation, 2. Security Server & Keys -->
                @if (engineConfigExpanded()) {
                  <div class="mt-1 ml-4 pl-3 border-l-2 border-emerald-200 space-y-1 animate-in fade-in duration-150">
                    <a
                      routerLink="/engine"
                      [routerLinkActiveOptions]="{ exact: true }"
                      routerLinkActive="bg-emerald-50 text-emerald-800 border-emerald-400/60 font-bold shadow-xs"
                      class="flex items-center gap-2.5 px-3 py-2 rounded-xl text-xs text-slate-600 hover:text-emerald-700 hover:bg-emerald-50/50 transition border border-transparent"
                    >
                      <span class="w-4 h-4 rounded-full bg-emerald-100 text-emerald-800 text-[10px] font-bold flex items-center justify-center shrink-0">1</span>
                      <span>Prerequisites & Install</span>
                    </a>

                    <a
                      routerLink="/engine/security-server"
                      routerLinkActive="bg-emerald-50 text-emerald-800 border-emerald-400/60 font-bold shadow-xs"
                      class="flex items-center gap-2.5 px-3 py-2 rounded-xl text-xs text-slate-600 hover:text-emerald-700 hover:bg-emerald-50/50 transition border border-transparent"
                    >
                      <span class="w-4 h-4 rounded-full bg-teal-100 text-teal-800 text-[10px] font-bold flex items-center justify-center shrink-0">2</span>
                      <span>Security Server & Keys</span>
                    </a>
                  </div>
                }
              </div>

              <a
                routerLink="/profile"
                routerLinkActive="bg-emerald-50 text-emerald-800 border-emerald-400/60 font-bold shadow-sm"
                class="flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-xs text-slate-600 hover:text-emerald-700 hover:bg-emerald-50/60 transition-all border border-transparent nav-link-glow"
              >
                <svg class="w-4 h-4 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                My Profile & Security
              </a>

              <a
                routerLink="/dsr"
                routerLinkActive="bg-emerald-50 text-emerald-800 border-emerald-400/60 font-bold shadow-sm"
                class="flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-xs text-slate-600 hover:text-emerald-700 hover:bg-emerald-50/60 transition-all border border-transparent nav-link-glow"
              >
                <svg class="w-4 h-4 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                </svg>
                Data Sharing Requests
              </a>

              <a
                routerLink="/services"
                routerLinkActive="bg-emerald-50 text-emerald-800 border-emerald-400/60 font-bold shadow-sm"
                class="flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-xs text-slate-600 hover:text-emerald-700 hover:bg-emerald-50/60 transition-all border border-transparent nav-link-glow"
              >
                <svg class="w-4 h-4 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
                Service Catalog
              </a>

              @if (authService.canManageOnboarding() || authService.hasRole('SuperAdmin')) {
                <a
                  routerLink="/onboarding"
                  routerLinkActive="bg-emerald-50 text-emerald-800 border-emerald-400/60 font-bold shadow-sm"
                  class="flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-xs text-slate-600 hover:text-emerald-700 hover:bg-emerald-50/60 transition-all border border-transparent nav-link-glow"
                >
                  <svg class="w-4 h-4 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                  Institution Registration
                </a>
              }

              <a
                routerLink="/reports"
                routerLinkActive="bg-emerald-50 text-emerald-800 border-emerald-400/60 font-bold shadow-sm"
                class="flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-xs text-slate-600 hover:text-emerald-700 hover:bg-emerald-50/60 transition-all border border-transparent nav-link-glow"
              >
                <svg class="w-4 h-4 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 01-2-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Governance Reports
              </a>

              <!-- Governance & SLA Links -->
              <div class="text-[10px] font-bold text-slate-400 uppercase tracking-wider px-3 pt-4 mb-2">Governance & SLAs</div>

              <a
                routerLink="/sla"
                routerLinkActive="bg-emerald-50 text-emerald-800 border-emerald-400/60 font-bold shadow-sm"
                class="flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-xs text-slate-600 hover:text-emerald-700 hover:bg-emerald-50/60 transition-all border border-transparent nav-link-glow"
              >
                <svg class="w-4 h-4 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                Service Level Agreements
              </a>

              <a
                routerLink="/attachment-types"
                routerLinkActive="bg-emerald-50 text-emerald-800 border-emerald-400/60 font-bold shadow-sm"
                class="flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-xs text-slate-600 hover:text-emerald-700 hover:bg-emerald-50/60 transition-all border border-transparent nav-link-glow"
              >
                <svg class="w-4 h-4 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                </svg>
                Compliance Attachments
              </a>

              <!-- Administration / Institutional Management Links -->
              @if (authService.hasPermission('can manage workflows') || authService.canManageOnboarding() || authService.hasRole('SuperAdmin') || authService.isInternal() || authService.isInstitutionAdmin()) {
                <div class="text-[10px] font-bold text-slate-400 uppercase tracking-wider px-3 pt-4 mb-2">
                  {{ authService.isInstitutionAdmin() && !authService.hasRole('SuperAdmin') && !authService.isInternal() ? 'Institutional Admin' : 'Administration' }}
                </div>

                @if (authService.canManageOnboarding() || authService.hasRole('SuperAdmin') || authService.isInternal()) {
                  <a
                    routerLink="/institutions"
                    routerLinkActive="bg-emerald-50 text-emerald-800 border-emerald-400/60 font-bold shadow-sm"
                    class="flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-xs text-slate-600 hover:text-emerald-700 hover:bg-emerald-50/60 transition-all border border-transparent nav-link-glow"
                  >
                    <svg class="w-4 h-4 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                    Institutions Directory
                  </a>
                }

                @if (authService.hasPermission('can manage workflows') || authService.hasRole('SuperAdmin')) {
                  <a
                    routerLink="/workflows"
                    routerLinkActive="bg-emerald-50 text-emerald-800 border-emerald-400/60 font-bold shadow-sm"
                    class="flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-xs text-slate-600 hover:text-emerald-700 hover:bg-emerald-50/60 transition-all border border-transparent nav-link-glow"
                  >
                    <svg class="w-4 h-4 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
                    </svg>
                    Workflows Builder
                  </a>
                }

                @if (authService.hasPermission('can assign role') || authService.hasRole('SuperAdmin') || authService.isInstitutionAdmin()) {
                  <a
                    routerLink="/users"
                    routerLinkActive="bg-emerald-50 text-emerald-800 border-emerald-400/60 font-bold shadow-sm"
                    class="flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-xs text-slate-600 hover:text-emerald-700 hover:bg-emerald-50/60 transition-all border border-transparent nav-link-glow"
                  >
                    <svg class="w-4 h-4 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                    </svg>
                    {{ authService.isInstitutionAdmin() && !authService.hasRole('SuperAdmin') ? 'Institutional Users' : 'User Management' }}
                  </a>
                }

                @if (authService.hasRole('SuperAdmin')) {
                  <a
                    routerLink="/roles"
                    routerLinkActive="bg-emerald-50 text-emerald-800 border-emerald-400/60 font-bold shadow-sm"
                    class="flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-xs text-slate-600 hover:text-emerald-700 hover:bg-emerald-50/60 transition-all border border-transparent nav-link-glow"
                  >
                    <svg class="w-4 h-4 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                    Role & RBAC Matrix
                  </a>
                }
              }

              @if (authService.isInternal() || authService.hasRole('SuperAdmin')) {
                <a
                  routerLink="/audits"
                  routerLinkActive="bg-emerald-50 text-emerald-800 border-emerald-400/60 font-bold shadow-sm"
                  class="flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-xs text-slate-600 hover:text-emerald-700 hover:bg-emerald-50/60 transition-all border border-transparent nav-link-glow"
                >
                  <svg class="w-4 h-4 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Security Audit Logs
                </a>
              }
            </nav>
          </div>

          <!-- Bottom Footer Info -->
          <div class="p-4 border-t border-slate-200 bg-slate-50 text-[11px] text-slate-500 shrink-0">
            <div class="font-bold text-slate-700">ZAN-X CHANGE v2.4</div>
            <div>Revolutionary e-Gov Platform</div>
          </div>
        </aside>

        <!-- Main Content Area (Scrolls independently while sidebar remains stationary) -->
        <main class="flex-1 min-h-0 overflow-y-auto p-4 sm:p-6 lg:p-8 bg-slate-50">
          <router-outlet></router-outlet>
        </main>
      </div>
    </div>
  `
})
export class LayoutComponent {
  authService = inject(AuthService);
  toastService = inject(ToastService);
  router = inject(Router);

  mobileSidebarOpen = signal<boolean>(false);
  showReviewModal = signal<boolean>(false);
  engineConfigExpanded = signal<boolean>(true);

  toggleEngineConfig(event: Event) {
    event.stopPropagation();
    this.engineConfigExpanded.update(v => !v);
  }

  isEngineConfigActive(): boolean {
    const url = this.router.url;
    return url.startsWith('/engine');
  }


  toggleMobileSidebar() {
    this.mobileSidebarOpen.update(v => !v);
  }

  closeMobileSidebar() {
    this.mobileSidebarOpen.set(false);
  }

  getUserInitials(): string {
    const name = this.authService.userName();
    const parts = name.trim().split(' ');
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return name.substring(0, 2).toUpperCase();
  }

  openSlaReview() {
    this.showReviewModal.set(true);
  }

  closeSlaReview() {
    this.showReviewModal.set(false);
  }

  onAcceptMandatorySla(consent: SlaPolicyConsent) {
    this.authService.acceptSla().subscribe({
      next: (res) => {
        this.toastService.success(
          'Master SLA and Zanzibar Data Governance Policy accepted. Platform access has been unlocked.'
        );
      },
      error: (err) => {
        this.toastService.error(
          err.error?.message || 'Could not record SLA agreement. Please try again.'
        );
      }
    });
  }

  logout() {
    this.authService.logout();
    this.router.navigate(['/login']);
  }
}
